import { Refresh } from "@mui/icons-material";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  CheckboxTemplate,
  NumberTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import { IRichLogData } from "@ryde/shared";

import { RichLogsCollapsibleFilterPanel } from "./rich-logs.collapsible-filter-panel";
import { RichLogCommaModeDropdown } from "./rich-logs.comma-mode-dropdown";
import { RichLogLevelCheckboxes } from "./rich-logs.level-checkboxes";
import { IRichLogsFilter, RichLogFormFields } from "./rich-logs.models";
import { RichLogSortDropdown } from "./rich-logs.sort-dropdown";
import { RichLogStepDropdown } from "./rich-logs.step-dropdown";
import { getUniqueLogStepOptions } from "./rich-logs.utils";

interface IRichLogFilters {
  isFinished?: boolean;
  logData: IRichLogData | undefined;
  isStatsCollapsed: boolean;
  hasExternalLogs?: boolean;
  bottomContent?: ReactNode;
  onRefreshLogsClick?: () => void;
  isRefreshLogsDisabled?: boolean;
  /** When true, show “AI hints only” filter (e.g. price-rate search drawer). */
  withAiHintsOnlyToggle?: boolean;
  searchFormLabel?: string;
  hideSearchField?: boolean;
  hideGroupField?: boolean;
  collapsedPanelLabel?: string;
  sectionEmoji?: string;
  defaultFiltersCollapsed?: boolean;
  syncFiltersCollapseWithStats?: boolean;
}

export const RichLogsFilters: FC<IRichLogFilters> = ({
  isFinished,
  logData,
  isStatsCollapsed,
  hasExternalLogs,
  bottomContent,
  onRefreshLogsClick,
  isRefreshLogsDisabled,
  withAiHintsOnlyToggle,
  searchFormLabel,
  hideSearchField,
  hideGroupField,
  collapsedPanelLabel,
  sectionEmoji = "📋",
  defaultFiltersCollapsed,
  syncFiltersCollapseWithStats = false,
}) => {
  const { t } = useTranslation();
  const { reset, setValue, watch } = useFormContext<IRichLogsFilter>();
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(
    defaultFiltersCollapsed ?? isStatsCollapsed,
  );
  const sectionTitle =
    collapsedPanelLabel ??
    t("rideProduction.logFilters", { defaultValue: "Log filters" });
  const selectedStep = watch(RichLogFormFields.step);

  const stepOptions = useMemo(
    () => getUniqueLogStepOptions(logData?.records),
    [logData?.records],
  );

  useEffect(() => {
    if (syncFiltersCollapseWithStats) {
      setIsFiltersCollapsed(isStatsCollapsed);
    }
  }, [isStatsCollapsed, syncFiltersCollapseWithStats]);

  useEffect(() => {
    if (
      selectedStep &&
      !stepOptions.some(option => option.value === selectedStep)
    ) {
      setValue(RichLogFormFields.step, undefined);
    }
  }, [selectedStep, setValue, stepOptions]);

  const handleResetValuesClick = (): void => {
    reset();
  };

  const toggleFilters = (): void => {
    setIsFiltersCollapsed(value => !value);
  };

  return (
    <div className="log__filters-container">
      <RichLogsCollapsibleFilterPanel
        className="log__filters-panel"
        collapsed={isFiltersCollapsed}
        collapseLabel={t("rideProduction.collapseLogFilters", {
          defaultValue: "Hide log filters",
        })}
        emoji={sectionEmoji}
        expandLabel={t("rideProduction.expandLogFilters", {
          defaultValue: "Show log filters",
        })}
        onToggle={toggleFilters}
        title={sectionTitle}
      >
        <div className="log__filters">
          <div className="log__filters--multiselect-container">
            <RichLogLevelCheckboxes
              logData={logData}
              showLoader={!isFinished && !hasExternalLogs}
            />
          </div>

          <div className="log__filters--search-row">
            {!hideSearchField && (
              <div className="log__filters--search-field">
                <TextboxTemplate
                  formLabel={
                    searchFormLabel ?? t("rideProduction.formLabel.searchText")
                  }
                  name={RichLogFormFields.searchText}
                />
              </div>
            )}
            <RichLogCommaModeDropdown />
            {!hideGroupField && (
              <div className="log__filters--group-field">
                <NumberTemplate
                  formLabel={t("rideProduction.formLabel.group")}
                  name={RichLogFormFields.group}
                />
              </div>
            )}
          </div>

          <div className="log__filters--sort-step-row">
            <RichLogSortDropdown />
            <RichLogStepDropdown options={stepOptions} />
          </div>

          <div className="log__filters--debug-checkbox-container">
            <CheckboxTemplate
              name={RichLogFormFields.showDebugInfo}
              formLabel="Show debug info"
            />
            {withAiHintsOnlyToggle ? (
              <CheckboxTemplate
                name={RichLogFormFields.withAiHintsOnly}
                formLabel={t("ride.priceRateSearchWithAiHintsOnly")}
              />
            ) : null}
          </div>

          <div className="log__filters--reset-button-container">
            {onRefreshLogsClick ? (
              <div className="log__filters--reset-actions">
                <button
                  type="button"
                  className="log__filters-refresh-btn"
                  onClick={onRefreshLogsClick}
                  disabled={isRefreshLogsDisabled}
                  title={t("general.REFRESH")}
                  aria-label={t("general.REFRESH")}
                >
                  <Refresh fontSize="small" />
                </button>
                <button
                  type="button"
                  className="log__filters-reset-btn"
                  onClick={handleResetValuesClick}
                >
                  {t("rideProduction.formLabel.resetToDefaults")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="log__filters-reset-btn"
                onClick={handleResetValuesClick}
              >
                {t("rideProduction.formLabel.resetToDefaults")}
              </button>
            )}
          </div>
        </div>
      </RichLogsCollapsibleFilterPanel>
      {bottomContent}
    </div>
  );
};
